import { useEffect, useRef } from "react";
import { useLocalParticipant } from "@livekit/components-react";
import { Track } from "livekit-client";
import { api } from "@/lib/api";

/**
 * LivePreviewPublisher — Live Preview Thumbnails v1 (host side).
 *
 * Mounted INSIDE <LiveKitRoom> (needs RoomContext, same pattern as
 * HostTopBarMic). Every 10s it grabs a frame from the host's local camera
 * track, downscales it to 480px JPEG (~40KB) on a canvas, and POSTs it to
 * /live/{id}/preview. Purely local capture — zero LiveKit egress cost, no
 * effect on the stream. Skips when the tab is hidden, the camera is muted,
 * or a previous upload is still in flight. Renders nothing.
 */
export default function LivePreviewPublisher({ streamId, isHost, canPublish }) {
  const { localParticipant } = useLocalParticipant();
  const busyRef = useRef(false);

  useEffect(() => {
    if (!isHost || !canPublish || !localParticipant || !streamId) return undefined;
    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    const canvas = document.createElement("canvas");
    let attachedTrack = null;
    let stopped = false;

    const capture = async () => {
      if (stopped || busyRef.current || document.hidden) return;
      const pub = localParticipant.getTrackPublication?.(Track.Source.Camera);
      const track = pub?.track;
      if (!track || pub?.isMuted) return;
      if (attachedTrack !== track) {
        try { attachedTrack?.detach(video); } catch { /* noop */ }
        track.attach(video);
        attachedTrack = track;
        try { await video.play(); } catch { /* autoplay quirks are fine */ }
      }
      if (!video.videoWidth || !video.videoHeight) return;
      const w = 480;
      const h = Math.round((video.videoHeight / video.videoWidth) * w);
      canvas.width = w;
      canvas.height = h;
      canvas.getContext("2d").drawImage(video, 0, 0, w, h);
      const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.6));
      if (!blob || stopped) return;
      busyRef.current = true;
      try {
        const form = new FormData();
        form.append("file", blob, "preview.jpg");
        await api.post(`/live/${streamId}/preview`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } catch { /* best-effort — never disturb the live */ }
      busyRef.current = false;
    };

    const first = setTimeout(capture, 3000);
    const timer = setInterval(capture, 10_000);
    return () => {
      stopped = true;
      clearTimeout(first);
      clearInterval(timer);
      try { attachedTrack?.detach(video); } catch { /* noop */ }
    };
  }, [isHost, canPublish, localParticipant, streamId]);

  return null;
}
